/*
 * Pepper-style mobile menu (wearpepper.com Dawn menu-drawer behaviour, measured in reference/pepper/menu/):
 * - the header hamburger (aria-controls="header-sidebar-menu") toggles it, its icon turns into a cross
 * - panel + overlay start right under the pinned header and fade in/out over 0.3s (class is-opening, like Dawn's
 *   "menu-opening"); page scroll is locked while open; overlay click / Escape / resize to desktop close it
 * - each parent link is an accordion (height 0.2s, opacity 0.5s), independent from the others
 * Same file as shopify-theme/assets/pepper-menu.js — keep both in sync.
 */
(() => {
  if (window.customElements.get("pepper-menu")) return;
  const DURATION = 300;

  class PepperMenu extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      // a re-rendered header (theme editor) brings a new copy: drop the previous one
      document.querySelectorAll("pepper-menu").forEach((m) => {
        if (m !== this && m.id === this.id) { if (m.hide) m.hide(true); m.remove(); }
      });
      // live at the end of <body> so no sticky/transformed ancestor clips the fixed panel
      if (this.parentElement !== document.body) queueMicrotask(() => document.body.appendChild(this));
      this.drawer = this.querySelector(".ppm__drawer");
      this.addEventListener("click", (event) => {
        if (event.target.closest("[data-ppm-close]")) return this.hide();
        const head = event.target.closest(".ppm__head");
        if (head) this.toggleItem(head);
      });
    }

    get controls() {
      return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`)).filter((c) => !this.contains(c));
    }

    position() {
      // Pepper: the panel's top is the bottom of the (pinned) header
      const header = document.querySelector("store-header") || document.querySelector(".shopify-section--header, header");
      const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      this.style.setProperty("--ppm-top", `${top}px`);
    }

    toggle(trigger) {
      if (this.hasAttribute("open")) this.hide();
      else this.show(trigger);
    }

    show(trigger) {
      clearTimeout(this._timer);
      this._trigger = trigger || this.controls[0];
      this.position();
      this.setAttribute("open", "");
      document.documentElement.classList.add("ppm-is-open");
      this.controls.forEach((c) => c.setAttribute("aria-expanded", "true"));
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add("is-opening")));
      this.drawer.scrollTop = 0;
      this.drawer.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent("dialog:after-show", { bubbles: true }));
    }

    hide(instant) {
      if (!this.hasAttribute("open")) return;
      this.classList.remove("is-opening");
      document.documentElement.classList.remove("ppm-is-open");
      this.controls.forEach((c) => c.setAttribute("aria-expanded", "false"));
      const done = () => {
        this.removeAttribute("open");
        this.dispatchEvent(new CustomEvent("dialog:after-hide", { bubbles: true }));
      };
      clearTimeout(this._timer);
      if (instant) done();
      else this._timer = setTimeout(done, DURATION);
      if (this._trigger && !instant) this._trigger.focus({ preventScroll: true });
    }

    toggleItem(head) {
      const item = head.closest(".ppm__item--parent");
      const more = item.querySelector(".ppm__more");
      const open = head.getAttribute("aria-expanded") !== "true";
      head.setAttribute("aria-expanded", String(open));
      clearTimeout(more._t);
      if (open) {
        more.hidden = false;
        item.classList.add("is-open");
        more.style.height = `${more.scrollHeight}px`;
        more._t = setTimeout(() => (more.style.height = "auto"), 200);
      } else {
        more.style.height = `${more.scrollHeight}px`;
        void more.offsetHeight;
        item.classList.remove("is-open");
        more.style.height = "0px";
        more._t = setTimeout(() => (more.hidden = true), 500);
      }
    }
  }

  window.customElements.define("pepper-menu", PepperMenu);

  // one set of document listeners for whichever menu is current
  const current = () => document.querySelector("pepper-menu");
  document.addEventListener("click", (event) => {
    const menu = current();
    const toggle = menu && event.target.closest && event.target.closest(`[aria-controls="${menu.id}"]`);
    if (toggle && !menu.contains(toggle)) {
      event.preventDefault();
      event.stopPropagation();
      menu.toggle(toggle);
    }
  }, true);
  document.addEventListener("keydown", (event) => {
    const menu = current();
    if (event.key === "Escape" && menu && menu.hasAttribute("open")) menu.hide();
  });
  window.matchMedia("(min-width: 1150px)").addEventListener("change", (mq) => mq.matches && current() && current().hide(true));
  window.addEventListener("pageshow", (event) => event.persisted && current() && current().hide(true));
})();

/* Country / currency selector (snippets/pepper-localization.liquid): open / close the list. The options are submit
   buttons of Shopify's localization form, so choosing a country needs no script. */
(() => {
  if (window.__pepperLocalization) return;
  window.__pepperLocalization = true;
  const closeAll = (except) => {
    document.querySelectorAll("[data-pl] [data-pl-toggle][aria-expanded='true']").forEach((btn) => {
      if (btn === except) return;
      btn.setAttribute("aria-expanded", "false");
      const panel = btn.closest("[data-pl]").querySelector("[data-pl-panel]");
      if (panel) panel.hidden = true;
    });
  };
  document.addEventListener("click", (event) => {
    const toggle = event.target.closest && event.target.closest("[data-pl-toggle]");
    if (toggle) {
      event.preventDefault();
      const panel = toggle.closest("[data-pl]").querySelector("[data-pl-panel]");
      const open = toggle.getAttribute("aria-expanded") !== "true";
      closeAll(toggle);
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      panel.hidden = !open;
      /* like wearpepper.com, the list always opens at its top (no scroll to the current country) */
      if (open) {
        const list = panel.querySelector(".pl__list");
        if (list) list.scrollTop = 0;
      }
      return;
    }
    if (!(event.target.closest && event.target.closest("[data-pl-panel]"))) closeAll();
  });
  document.addEventListener("keydown", (event) => { if (event.key === "Escape") closeAll(); });
})();
